/**
 * Pure time helpers. The grid works in "minutes from midnight of the displayed day";
 * everything else stays ISO strings. No timezone conversion happens here — event ISO
 * strings carry the clinic's offset and `new Date()` renders them in the browser's zone,
 * which matches today's scheduler. Clinic-timezone rendering is a Phase 3 item.
 */

export const MINUTES_PER_DAY = 24 * 60;

export function minutesOfDay(iso: string): number {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
}

/** `YYYY-MM-DD` of an ISO timestamp in the browser's zone. */
export function isoDate(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number);
  const next = new Date(y, m - 1, d + days);
  return isoDate(next);
}

/** Sunday of the week containing `date`: V3's weeks start on Sunday, as classic's (ENG-2409). */
export function startOfWeek(date: string): string {
  const [y, m, d] = date.split("-").map(Number);
  return addDays(date, -new Date(y, m - 1, d).getDay());
}

export function formatMinutes(minutes: number): string {
  const h24 = Math.floor(minutes / 60);
  const m = minutes % 60;
  const suffix = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return m === 0
    ? `${h12} ${suffix}`
    : `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
}

export function formatTimeRange(startIso: string, endIso: string): string {
  const start = minutesOfDay(startIso);
  const end = minutesOfDay(endIso);
  const meridiem = (m: number) => (Math.floor(m / 60) >= 12 ? "p" : "a");
  const clock = (m: number) => {
    const h24 = Math.floor(m / 60);
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return `${h12}:${String(m % 60).padStart(2, "0")}`;
  };
  // The old grid always states the real range with minutes ("03:00 — 04:00"), never a
  // bare hour, and the cell is the first place anyone reads a time. The meridiem is
  // dropped from the start only when both ends share it, so "3:00–4:00p" stays short
  // without becoming ambiguous across noon.
  const sameHalf = meridiem(start) === meridiem(end);
  return `${clock(start)}${sameHalf ? "" : meridiem(start)}–${clock(end)}${meridiem(end)}`;
}

/** "9:15a" — the start of a card's time, in the same shape as `formatTimeRange`. */
export function formatStartTime(startIso: string): string {
  const m = minutesOfDay(startIso);
  const h24 = Math.floor(m / 60);
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m % 60).padStart(2, "0")}${h24 >= 12 ? "p" : "a"}`;
}

/**
 * "Wednesday, September 16th" — the old preview's `format(date, "EEEE, LLLL do")`.
 * `toLocaleDateString` has no ordinal, so the day read "September 16" until a browser
 * comparison against the old grid showed the difference (ENG-1983).
 */
export function formatLongDate(iso: string): string {
  const date = new Date(iso);
  const day = date.getDate();
  // 11th, 12th and 13th break the last-digit rule.
  const teens = day % 100 >= 11 && day % 100 <= 13;
  const suffix = teens ? "th" : { 1: "st", 2: "nd", 3: "rd" }[day % 10] || "th";
  // Asking for weekday and month in one call lets the locale choose their order
  // ("September Wednesday"); the old grid's pattern is fixed, so build it from parts.
  const weekday = date.toLocaleDateString(undefined, { weekday: "long" });
  const month = date.toLocaleDateString(undefined, { month: "long" });
  return `${weekday}, ${month} ${day}${suffix}`;
}

/** "10h 30m", "9h", "45m": how long a shift or an event runs, for a label. */
export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return [h && `${h}h`, m && `${m}m`].filter(Boolean).join(" ") || "0m";
}

export function durationMinutes(startIso: string, endIso: string): number {
  return Math.round(
    (new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000
  );
}

/**
 * An instant as the clinic's wall clock: its day and minutes from midnight in `timeZone`.
 *
 * The old dialog converts every availability moment this way
 * (`utils/staff-availability.util.ts`, `convertDateInTimezoneToCurrent(moment,
 * clinic.timezone)`), and V3's grid draws events in the same frame (`map-events.ts`). So a
 * chip reads the hour the clinic means, and the card lands where the chip said. The
 * moment's own text is no use: under `X-Timezone: practice` it is in the PRACTICE's zone.
 * Measured 2026-09-19 against the old dialog: browser-local times ran 3 h late on an EDT
 * machine for a Pacific clinic (ENG-2020 D12). `clinicMoment` is the way back.
 */
export function wallClock(
  moment: string,
  timeZone: string | null | undefined
): { date: string; minutes: number } {
  const at = new Date(moment);
  if (timeZone) {
    try {
      const parts = Object.fromEntries(
        new Intl.DateTimeFormat("en-US", {
          timeZone,
          hourCycle: "h23",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit"
        })
          .formatToParts(at)
          .map((p) => [p.type, p.value])
      );
      return {
        date: `${parts.year}-${parts.month}-${parts.day}`,
        minutes: Number(parts.hour) * 60 + Number(parts.minute)
      };
    } catch {
      // An unknown zone name falls through to the browser's clock.
    }
  }
  return { date: isoDate(at), minutes: minutesOfDay(moment) };
}

/**
 * The zone the grid's "now" and "today" are read in (ENG-2034). Events are drawn at their
 * clinic's wall time (`wallClock` above), so the clock beside them has to be the clinic's
 * too: read from the browser it sat 3 h late among a Pacific clinic's appointments on an
 * Eastern laptop, while Room Traffic's clock, one click away, showed clinic time.
 *
 * The clinics on screen usually share a zone. When they do not (local practice 800 has a
 * Pacific clinic and three Eastern ones), one grid still has one now-line, so it follows
 * the CURRENT clinic, the one named in the top bar, while that clinic is on the grid. That
 * is the clock Room Traffic shows, so the two agree by construction. With the current
 * clinic filtered out it is the first selected clinic's. A line per zone is not built.
 * `undefined` (nothing selected, catalog not loaded) means the browser's clock.
 */
export function gridTimeZone(
  clinics: readonly { id: number; timezone: string }[],
  selectedIds: readonly number[],
  currentClinicId?: number | null
): string | undefined {
  const shown = clinics.filter((c) => selectedIds.includes(c.id));
  return (shown.find((c) => c.id === currentClinicId) ?? shown[0])?.timezone;
}

/** The day and the minutes from midnight it is right now on the grid's clock. */
export function gridNow(
  timeZone: string | null | undefined,
  at: Date = new Date()
): { date: string; minutes: number } {
  return wallClock(at.toISOString(), timeZone);
}

/**
 * "Now" at a clinic as a number: the clinic's wall clock, in the same frame as
 * `new Date(event.start).getTime()`, to minute precision.
 *
 * Events are drawn at their own clinic's wall time as browser-local instants (`wallClock`
 * above), so they are not true instants and comparing one with a bare `Date.now()` is off by
 * the zone gap: 3 h for a Pacific clinic on an Eastern laptop. This reads the clinic's
 * wall clock (`gridNow`) and builds the browser-local instant that shows it, which is how an
 * event's own time is built. No zone, or one `Intl` does not know, is the browser's clock.
 * Leaving out `at` reads `Date.now()` (not `new Date()`, which skips it), so a test that
 * pins `Date.now` pins every caller.
 */
export function clinicNowMs(
  timeZone: string | null | undefined,
  at: Date = new Date(Date.now())
): number {
  const { date, minutes } = gridNow(timeZone, at);
  const [y, m, d] = date.split("-").map(Number);
  return new Date(y, m - 1, d, 0, minutes).getTime();
}

/** The UTC offset `timeZone` has at an instant, as `+HH:MM` / `-HH:MM`. */
function offsetAt(instant: Date, timeZone: string): string {
  const { date, minutes } = wallClock(instant.toISOString(), timeZone);
  const [y, m, d] = date.split("-").map(Number);
  const asUtc = Date.UTC(y, m - 1, d, 0, minutes);
  const diff = Math.round((asUtc - instant.getTime()) / 60000);
  const sign = diff < 0 ? "-" : "+";
  const abs = Math.abs(diff);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

/**
 * A clinic wall time as the moment the backend stores: `yyyy-MM-dd HH:mm:ss±HH:MM` with
 * the CLINIC's offset on that day (so DST is the clinic's, not the browser's).
 *
 * Every V3 surface works in clinic wall time — the grid, the form, Find Next Available —
 * so a write that stamps the browser's offset books at the wrong instant whenever the two
 * zones differ: measured 3 h early, EDT browser, Pacific clinic, and a dedicated-slot
 * booking refused outright (ENG-2020 D12). The old form converts the same way
 * (`appointment-form.component.tsx:952-974`, `convertDateFromCurrentTimezoneToTimezone`).
 *
 * The offset is found by guessing with the browser-independent UTC reading and correcting
 * once, which lands on the right side of a DST change. No zone, or one `Intl` does not
 * know, falls back to the browser's offset — what V3 sent before.
 */
export function clinicMoment(
  date: string,
  minutes: number,
  timeZone: string | null | undefined
): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const clock = `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}:00`;
  const [y, m, d] = date.split("-").map(Number);
  const browser = () => {
    const at = new Date(y, m - 1, d, 0, minutes);
    const off = -at.getTimezoneOffset();
    const abs = Math.abs(off);
    return `${off < 0 ? "-" : "+"}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
  };
  let offset: string;
  try {
    if (!timeZone) throw new Error("no zone");
    const guess = new Date(Date.UTC(y, m - 1, d, 0, minutes));
    const first = offsetAt(guess, timeZone);
    const toMs = (o: string) =>
      (o[0] === "-" ? -1 : 1) *
      (Number(o.slice(1, 3)) * 60 + Number(o.slice(4))) *
      60000;
    // The real instant is the wall time minus its offset; re-read the offset there.
    offset = offsetAt(new Date(guess.getTime() - toMs(first)), timeZone);
  } catch {
    offset = browser();
  }
  return `${date} ${clock}${offset}`;
}
